import { type QueryClient } from "@tanstack/react-query";

import { isBoardAccessDenied } from "../boardAccess";
import { boardToCache, type Board } from "../BoardRecord";
import { type BoardService } from "../BoardService";

import { boardQueryKeys } from "./boardQueryKeys";

/**
 * Puts the record a write's response carried into the board's query, unless the
 * query already holds a record the server stored later (`boardToCache`),
 * so the page shows what the write changed without waiting for a refetch.
 *
 * Setting a query's data clears its error, so while the query's error is an
 * access error nothing is written: that error stays until a fetch of the board
 * succeeds.
 */
export function cacheBoardWriteResponse(
  queryClient: QueryClient,
  record: Board,
): void {
  const queryKey = boardQueryKeys.id(record.board_id, record.org_id);
  if (isBoardAccessDenied(queryClient.getQueryState(queryKey)?.error)) {
    return;
  }
  queryClient.setQueryData<Board>(queryKey, (existing) =>
    boardToCache(existing, record),
  );
}

/**
 * Puts a board that was just created into its query, so the page that opens it
 * shows it without a fetch, and invalidates the org's board lists so they show
 * it too.
 */
export function cacheCreatedBoard(
  queryClient: QueryClient,
  board: Board,
): void {
  queryClient.setQueryData<Board>(
    boardQueryKeys.id(board.board_id, board.org_id),
    board,
  );
  void queryClient.invalidateQueries({
    queryKey: boardQueryKeys.list(board.org_id),
  });
}

/**
 * Cancels the board query's fetch in flight, marks it stale without refetching,
 * then fetches the board once with no retries. The query keeps its record and
 * error until that fetch settles; a successful fetch replaces the record and
 * clears the error.
 */
export async function refreshBoardRecord(
  queryClient: QueryClient,
  boardService: BoardService,
  boardId: string,
  orgId: string,
): Promise<Board> {
  const queryKey = boardQueryKeys.id(boardId, orgId);
  await queryClient.cancelQueries({ queryKey, exact: true });
  await queryClient.invalidateQueries({
    queryKey,
    exact: true,
    refetchType: "none",
  });
  return queryClient.fetchQuery({
    queryKey,
    staleTime: 0,
    retry: false,
    queryFn: ({ signal }) =>
      boardService.getBoardById(boardId, orgId, { abortSignal: signal }),
  });
}

/**
 * Refetches the board's record and invalidates the org's board lists, ignoring
 * failures of either, so the page and the lists show what the server holds
 * after a write, including one whose response was lost.
 */
export async function refreshBoardAndInvalidateLists(
  queryClient: QueryClient,
  boardService: BoardService,
  boardId: string,
  orgId: string,
): Promise<void> {
  await Promise.allSettled([
    refreshBoardRecord(queryClient, boardService, boardId, orgId),
    (async () => {
      const queryKey = boardQueryKeys.list(orgId);
      await queryClient.cancelQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey });
    })(),
  ]);
}
